import { useInfiniteQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { EligibleStorageApiAdapter } from '../adapters/EligibleStorageApiAdapter';
import { EligibleStorage } from '../models/EligibleStorage';

interface UseEligibleStoragesProps {
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  date_from?: string;
  date_to?: string;
  product_id?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export const useEligibleStorages = ({
  searchFilter,
  search,
  date_from,
  date_to,
  product_id,
  sortBy = 'storage_date',
  sortDirection = 'desc',
}: UseEligibleStoragesProps = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const adapter = new EligibleStorageApiAdapter(httpClient);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'eligible-storages',
      searchFilter,
      search,
      date_from,
      date_to,
      product_id,
      sortBy,
      sortDirection,
    ],
    queryFn: ({ pageParam = 1 }) =>
      adapter.getEligibleStorages({
        searchFilter,
        search,
        date_from,
        date_to,
        product_id,
        sortBy,
        sortDirection,
        page: pageParam,
        limit: 25,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const storages: EligibleStorage[] =
    data?.pages.flatMap((page) => page.storages) ?? [];

  const total = data?.pages[0]?.total ?? 0;

  return {
    storages,
    isLoading,
    isFetching,
    error: error ? (error as Error).message : null,
    total,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
